import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../utils/constants';

const TrainingModule = ({ onBack, currentUser }) => {
  const [mode, setMode] = useState(currentUser?.rawRole === 'admin' ? 'admin' : 'employee');
  const [hrPositions, setHrPositions] = useState([]);
  const [manuals, setManuals] = useState([]);


  // Admin state
  const [modules, setModules] = useState([]);
  const [loadingModules, setLoadingModules] = useState(false);
  const [showModuleForm, setShowModuleForm] = useState(false);
  const [savingModule, setSavingModule] = useState(false);
  const [editingModule, setEditingModule] = useState(null);

  const emptyModuleForm = {
    title: '',
    description: '',
    items: []
  };
  const [moduleForm, setModuleForm] = useState(emptyModuleForm);

  // Employee state
  const [myModules, setMyModules] = useState([]);
  const [myProgress, setMyProgress] = useState({}); // module_id -> progress
  const [loadingMy, setLoadingMy] = useState(false);
  const [openModuleId, setOpenModuleId] = useState(null);

  React.useEffect(() => {
    const fetchPositions = async () => {
      try {
        const res = await axios.get(`${API}/hr/positions`);
        setHrPositions(res.data || []);
      } catch (e) {
        console.error('Error fetching HR positions:', e);
        setHrPositions([]);
      }
    };

    const fetchManuals = async () => {
      try {
        const res = await axios.get(`${API}/manuals`);
        setManuals(res.data || []);
      } catch (e) {
        console.error('Error fetching installation manuals:', e);
        setManuals([]);
      }
    };

    fetchPositions();
    fetchManuals();
  }, []);

  const fetchAdminModules = async () => {
    setLoadingModules(true);
    try {
      const res = await axios.get(`${API}/training/modules`);
      setModules(res.data || []);
    } catch (e) {
      console.error('Error loading training modules:', e);
      setModules([]);
    } finally {
      setLoadingModules(false);
    }
  };

  const fetchEmployeeModules = async () => {
    setLoadingMy(true);
    try {
      const [modsRes, progRes] = await Promise.all([
        axios.get(`${API}/training/my`),
        axios.get(`${API}/training/progress/my`)
      ]);

      const mods = modsRes.data || [];
      setMyModules(mods);

      const progArr = progRes.data || [];
      const progMap = {};
      for (const p of progArr) progMap[p.module_id] = p;
      setMyProgress(progMap);
    } catch (e) {
      console.error('Error loading my training:', e);
      setMyModules([]);
      setMyProgress({});
    } finally {
      setLoadingMy(false);
    }
  };

  // Admin assignments + progress (employee-centric)
  const [adminTab, setAdminTab] = useState('modules'); // 'modules' | 'assignments' | 'progress'
  const [selectedAdminEmployeeId, setSelectedAdminEmployeeId] = useState('');
  const [employeeAssignments, setEmployeeAssignments] = useState([]);
  const [loadingEmployeeAssignments, setLoadingEmployeeAssignments] = useState(false);
  const [selectedAssignModuleId, setSelectedAssignModuleId] = useState('');
  const [savingEmployeeAssign, setSavingEmployeeAssign] = useState(false);

  const [employeeProgress, setEmployeeProgress] = useState([]);
  const [loadingEmployeeProgress, setLoadingEmployeeProgress] = useState(false);

  const fetchEmployeeAssignments = async (employeeId) => {
    if (!employeeId) {
      setEmployeeAssignments([]);
      return;
    }
    setLoadingEmployeeAssignments(true);
    try {
      const res = await axios.get(`${API}/training/assignments/employee/${employeeId}`);
      setEmployeeAssignments(res.data || []);
    } catch (e) {
      console.error('Error fetching employee assignments:', e);
      setEmployeeAssignments([]);
    } finally {
      setLoadingEmployeeAssignments(false);
    }
  };

  const fetchEmployeeProgressAdmin = async (employeeId) => {
    if (!employeeId) {
      setEmployeeProgress([]);
      return;
    }
    setLoadingEmployeeProgress(true);
    try {
      const res = await axios.get(`${API}/training/progress/admin/${employeeId}`);
      setEmployeeProgress(res.data || []);
    } catch (e) {
      console.error('Error fetching employee progress:', e);
      setEmployeeProgress([]);
    } finally {
      setLoadingEmployeeProgress(false);
    }
  };

  const addModuleToEmployee = async () => {
    if (!selectedAdminEmployeeId || !selectedAssignModuleId) return;
    setSavingEmployeeAssign(true);
    try {
      await axios.post(`${API}/training/assignments`, {
        module_id: selectedAssignModuleId,
        employee_id: selectedAdminEmployeeId,
      });
      setSelectedAssignModuleId('');
      await fetchEmployeeAssignments(selectedAdminEmployeeId);
    } catch (e) {
      console.error('Error assigning module:', e);
      alert(e.response?.data?.detail || e.message);
    } finally {
      setSavingEmployeeAssign(false);
    }
  };

  const removeEmployeeAssignment = async (assignmentId) => {
    if (!assignmentId) return;
    try {
      await axios.delete(`${API}/training/assignments/${assignmentId}`);
      await fetchEmployeeAssignments(selectedAdminEmployeeId);
      // progress is preserved in DB by design
    } catch (e) {
      console.error('Error removing assignment:', e);
      alert(e.response?.data?.detail || e.message);
    }
  };


  const [activeEmployees, setActiveEmployees] = useState([]);



  const fetchActiveEmployees = async () => {
    try {
      const res = await axios.get(`${API}/employees/active`);
      setActiveEmployees(res.data || []);
    } catch (e) {
      console.error('Error fetching active employees:', e);
      setActiveEmployees([]);
    }
  };

  React.useEffect(() => {
    if (currentUser?.rawRole === 'admin') {
      fetchAdminModules();
      fetchActiveEmployees();
    } else {
      fetchEmployeeModules();
    }
  }, []);

  const positionTitle = (id) => hrPositions.find(p => p.id === id)?.title || id || '';

  const employeeLabel = (e) => {
    if (!e) return '';
    const name = `${e.first_name || ''} ${e.last_name || ''}`.trim();
    return name || e.email || e.id || '';
  };

  // Admin actions
  const openCreateModule = () => {
    setEditingModule(null);


    setModuleForm(emptyModuleForm);
    setShowModuleForm(true);
  };

  const openEditModule = (m) => {
    setEditingModule(m);
    setModuleForm({
      title: m.title || '',
      description: m.description || '',

      items: (m.items || []).map(it => ({
        id: it.id,
        title: it.title || '',
        url: it.url || '',
        view_mode: it.view_mode || 'new_tab',
        item_type: it.item_type || 'link',
        manual_id: it.manual_id || ''
      }))
    });
    setShowModuleForm(true);
  };

  const addItem = () => {
    setModuleForm({
      ...moduleForm,
      items: [...moduleForm.items, { id: `item_${Date.now()}`, title: '', url: '', view_mode: 'new_tab', item_type: 'link', manual_id: '' }]
    });
  };

  const updateItem = (idx, patch) => {
    const next = [...moduleForm.items];
    next[idx] = { ...next[idx], ...patch };
    setModuleForm({ ...moduleForm, items: next });
  };

  const removeItem = (idx) => {
    const next = [...moduleForm.items];
    next.splice(idx, 1);
    setModuleForm({ ...moduleForm, items: next });
  };

  const saveModule = async (e) => {
    e.preventDefault();
    if (!moduleForm.title.trim()) return alert('Title is required');


    setSavingModule(true);
    try {
      if (editingModule?.id) {
        await axios.put(`${API}/training/modules/${editingModule.id}`, {
          ...moduleForm,
          title: moduleForm.title.trim(),
          description: moduleForm.description.trim(),
        });
      } else {
        await axios.post(`${API}/training/modules`, {
          ...moduleForm,
          title: moduleForm.title.trim(),
          description: moduleForm.description.trim(),
        });
      }

      setShowModuleForm(false);
      await fetchAdminModules();
    } catch (e2) {
      console.error('Save training module failed:', e2);
      alert(e2.response?.data?.detail || e2.message);
    } finally {
      setSavingModule(false);
    }
  };

  const deleteModule = async (m) => {
    if (!window.confirm(`Delete training module "${m.title}"?`)) return;
    try {
      await axios.delete(`${API}/training/modules/${m.id}`);
      await fetchAdminModules();
    } catch (e) {
      console.error('Delete training module failed:', e);
      alert(e.response?.data?.detail || e.message);
    }
  };

  // Employee actions
  const toggleItem = async (moduleId, itemId, completed) => {
    try {
      const res = await axios.post(`${API}/training/progress/${moduleId}`, { item_id: itemId, completed });
      setMyProgress({ ...myProgress, [moduleId]: res.data });
    } catch (e) {
      console.error('Update progress failed:', e);
      alert(e.response?.data?.detail || e.message);
    }
  };

  const renderEmployeeModule = (m) => {
    const prog = myProgress[m.id];
    const completedIds = new Set(prog?.completed_item_ids || []);
    const total = (m.items || []).length;
    const done = (prog?.completed_item_ids || []).length;

    const isOpen = openModuleId === m.id;

    return (
      <div key={m.id} className="bg-white rounded-xl border border-emerald-100 shadow-sm">
        <button
          onClick={() => setOpenModuleId(isOpen ? null : m.id)}
          className="w-full text-left px-5 py-4 flex items-center justify-between"
        >
          <div>
            <div className="text-lg font-bold text-gray-900">{m.title}</div>

            <div className="text-xs text-gray-500 mt-1">Progress: {done} / {total} {prog?.completed ? '(Completed)' : ''}</div>
          </div>
          <div className="text-gray-500">{isOpen ? '▲' : '▼'}</div>
        </button>

        {isOpen && (
          <div className="px-5 pb-5">
            {m.description && <div className="text-sm text-gray-700 mb-4">{m.description}</div>}

            <div className="space-y-3">
              {(m.items || []).map((it) => {
                const checked = completedIds.has(it.id);
                return (
                  <div key={it.id} className="p-3 rounded-lg border border-gray-200 bg-gray-50">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => toggleItem(m.id, it.id, e.target.checked)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{it.title}</div>

                        {(it.item_type || 'link') === 'manual' ? (
                          it.manual_id ? (
                            <div className="mt-2">
                              <a
                                href={`${API}/manuals/${it.manual_id}/view`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-emerald-600 hover:text-emerald-700 text-sm underline"
                              >
                                📄 View Installation Manual
                              </a>
                            </div>
                          ) : null
                        ) : (it.url ? (
                          <div className="mt-2">
                            {it.view_mode === 'embed' ? (
                              <div className="rounded-lg overflow-hidden border border-gray-200 bg-white">
                                <iframe
                                  title={it.title}
                                  src={it.url}
                                  className="w-full h-64"
                                />
                              </div>
                            ) : (
                              <a
                                href={it.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-emerald-600 hover:text-emerald-700 text-sm underline"
                              >
                                🔗 {it.url}
                              </a>
                            )}
                          </div>
                        ) : null)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        )}
      </div>
    );
  };



  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white text-gray-800">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 flex items-center justify-between gap-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-emerald-700 hover:text-emerald-800 font-medium"
          >
            ← Back to Gears
          </button>

          {currentUser?.rawRole === 'admin' && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setMode('admin')}
                className={`px-3 py-2 rounded-md text-sm font-medium ${mode === 'admin' ? 'bg-emerald-700 text-white' : 'bg-white border border-emerald-200 text-emerald-800'}`}
              >
                Admin
              </button>
              <button
                onClick={() => setMode('employee')}
                className={`px-3 py-2 rounded-md text-sm font-medium ${mode === 'employee' ? 'bg-emerald-700 text-white' : 'bg-white border border-emerald-200 text-emerald-800'}`}
              >
                Employee Preview
              </button>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-emerald-100">
          <div className="p-6 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">🎓 Training</h1>
            <p className="text-sm text-gray-600 mt-1">Admin-managed training modules with assignment-based access and checklist tracking. Quiz is next phase.</p>
          </div>

          {mode === 'admin' && currentUser?.rawRole === 'admin' ? (
            <div className="p-6">
              <div className="flex items-center justify-between gap-4 mb-4">
                <div className="text-sm text-gray-600">{modules.length} modules</div>
                <button
                  onClick={openCreateModule}
                  className="px-4 py-2 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium"
                >
                  + Create Module
                </button>
              </div>

              <div className="flex items-center gap-2 mb-5">
                <button
                  onClick={() => setAdminTab('modules')}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${adminTab === 'modules' ? 'bg-emerald-700 text-white' : 'bg-white border border-emerald-200 text-emerald-800'}`}
                >
                  Modules
                </button>
                <button
                  onClick={() => setAdminTab('assignments')}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${adminTab === 'assignments' ? 'bg-emerald-700 text-white' : 'bg-white border border-emerald-200 text-emerald-800'}`}
                >
                  Assignments
                </button>
                <button
                  onClick={() => setAdminTab('progress')}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${adminTab === 'progress' ? 'bg-emerald-700 text-white' : 'bg-white border border-emerald-200 text-emerald-800'}`}
                >
                  Progress
                </button>
              </div>

              {adminTab === 'modules' ? (
                loadingModules ? (
                  <div className="text-gray-600">Loading modules...</div>
                ) : modules.length === 0 ? (
                  <div className="text-gray-600">No training modules yet.</div>
                ) : (
                  <div className="space-y-3">
                    {modules.map((m) => (
                      <div key={m.id} className="p-4 rounded-lg border border-gray-200 flex items-start justify-between gap-4">
                        <div>
                          <div className="font-bold text-gray-900">{m.title}</div>
                          {m.description && <div className="text-sm text-gray-700 mt-1">{m.description}</div>}
                          <div className="text-xs text-gray-500 mt-1">{(m.items || []).length} checklist items</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => openEditModule(m)} className="px-3 py-2 rounded-md bg-gray-100 hover:bg-gray-200 text-sm">Edit</button>
                          <button onClick={() => deleteModule(m)} className="px-3 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm">Delete</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : adminTab === 'assignments' ? (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg border border-emerald-200 bg-emerald-50">
                    <div className="font-semibold text-emerald-900">Assign modules to an employee</div>
                    <div className="text-sm text-emerald-800 mt-1">Select an employee, then add/remove modules. Progress is preserved if removed and re-assigned.</div>

                    <div className="mt-3 grid md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Employee</label>
                        <select
                          value={selectedAdminEmployeeId}
                          onChange={async (e) => {
                            const id = e.target.value;
                            setSelectedAdminEmployeeId(id);
                            await fetchEmployeeAssignments(id);
                          }}
                          className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="">Select employee...</option>
                          {(activeEmployees || []).map((e) => (
                            <option key={e.id} value={e.id}>{employeeLabel(e)}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Module</label>
                        <select
                          value={selectedAssignModuleId}
                          onChange={(e) => setSelectedAssignModuleId(e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                          disabled={!selectedAdminEmployeeId}
                        >
                          <option value="">Select module...</option>
                          {(modules || []).map((m) => (
                            <option key={m.id} value={m.id}>{m.title}</option>
                          ))}
                        </select>
                      </div>

                      <div className="flex items-end">
                        <button
                          onClick={addModuleToEmployee}
                          disabled={!selectedAdminEmployeeId || !selectedAssignModuleId || savingEmployeeAssign}
                          className="w-full px-4 py-2 rounded-md bg-emerald-700 hover:bg-emerald-800 disabled:bg-gray-400 text-white text-sm font-medium"
                        >
                          {savingEmployeeAssign ? 'Adding...' : 'Add Module'}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="font-semibold text-gray-900 mb-2">Assigned Modules</div>
                    {!selectedAdminEmployeeId ? (
                      <div className="text-sm text-gray-600">Select an employee to view assignments.</div>
                    ) : loadingEmployeeAssignments ? (
                      <div className="text-sm text-gray-600">Loading assignments...</div>
                    ) : employeeAssignments.length === 0 ? (
                      <div className="text-sm text-gray-600">No assigned modules for this employee.</div>
                    ) : (
                      <div className="space-y-2">
                        {employeeAssignments.map((a) => (
                          <div key={a.id} className="p-3 rounded-lg border border-gray-200 flex items-center justify-between">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{a.module_title || a.module_id}</div>
                              {a.assigned_date ? <div className="text-xs text-gray-600">Assigned {new Date(a.assigned_date).toLocaleString()}</div> : null}
                            </div>
                            <button
                              onClick={() => removeEmployeeAssignment(a.id)}
                              className="px-3 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg border border-emerald-200 bg-emerald-50">
                    <div className="font-semibold text-emerald-900">Employee Progress</div>
                    <div className="text-sm text-emerald-800 mt-1">Select an employee to view progress by module.</div>

                    <div className="mt-3">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Employee</label>
                      <select
                        value={selectedAdminEmployeeId}
                        onChange={async (e) => {
                          const id = e.target.value;
                          setSelectedAdminEmployeeId(id);
                          await fetchEmployeeProgressAdmin(id);
                        }}
                        className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="">Select employee...</option>
                        {(activeEmployees || []).map((e) => (
                          <option key={e.id} value={e.id}>{employeeLabel(e)}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {!selectedAdminEmployeeId ? (
                    <div className="text-sm text-gray-600">Select an employee to view progress.</div>
                  ) : loadingEmployeeProgress ? (
                    <div className="text-sm text-gray-600">Loading progress...</div>
                  ) : (
                    <div className="space-y-2">
                      {(employeeProgress || []).length === 0 ? (
                        <div className="text-sm text-gray-600">No progress yet for this employee.</div>
                      ) : (
                        employeeProgress.map((p) => {
                          const mod = (modules || []).find((m) => m.id === p.module_id);
                          const total = (mod?.items || []).length;
                          const done = (p.completed_item_ids || []).length;
                          const pct = total ? Math.round((done / total) * 100) : 0;
                          return (
                            <div key={p.id} className="p-3 rounded-lg border border-gray-200">
                              <div className="flex items-center justify-between gap-3">
                                <div>
                                  <div className="text-sm font-semibold text-gray-900">{mod?.title || p.module_id}</div>
                                  <div className="text-xs text-gray-600">{done}/{total} items • {pct}% {p.completed ? '• Completed' : ''}</div>
                                </div>
                                {p.completed_date ? (
                                  <div className="text-xs text-gray-600">{new Date(p.completed_date).toLocaleString()}</div>
                                ) : null}
                              </div>
                              <div className="mt-2 h-2 w-full bg-gray-100 rounded">
                                <div className="h-2 bg-emerald-600 rounded" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              )}


              {showModuleForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
                  <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                    <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 rounded-t-xl flex items-center justify-between">
                      <h2 className="text-xl font-bold text-gray-900">{editingModule ? 'Edit Training Module' : 'Create Training Module'}</h2>
                      <button onClick={() => setShowModuleForm(false)} className="text-gray-500 hover:text-gray-700 text-2xl font-bold">×</button>
                    </div>

                    <form onSubmit={saveModule} className="p-6 space-y-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                        <input
                          value={moduleForm.title}
                          onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                        />
                      </div>



                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                          value={moduleForm.description}
                          onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                          rows={3}
                        />
                      </div>

                      <div className="border border-gray-200 rounded-lg">
                        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                          <div className="font-semibold text-gray-900">Checklist Items</div>
                          <button type="button" onClick={addItem} className="px-3 py-2 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white text-sm">+ Add Item</button>
                        </div>
                        <div className="p-4 space-y-4">
                          {moduleForm.items.length === 0 ? (
                            <div className="text-sm text-gray-600">No items yet.</div>
                          ) : (
                            moduleForm.items.map((it, idx) => (
                              <div key={it.id} className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                                <div className="grid md:grid-cols-2 gap-3">
                                  <div className="md:col-span-2">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Item Title *</label>
                                    <input
                                      value={it.title}
                                      onChange={(e) => updateItem(idx, { title: e.target.value })}
                                      className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Item Type</label>
                                    <select
                                      value={it.item_type || 'link'}
                                      onChange={(e) => updateItem(idx, { item_type: e.target.value, manual_id: '', url: '' })}
                                      className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                                    >
                                      <option value="link">Link / Video URL</option>
                                      <option value="manual">Installation Manual (Production)</option>
                                    </select>
                                  </div>

                                  {(it.item_type || 'link') === 'link' ? (
                                    <>
                                      <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">URL (optional)</label>
                                        <input
                                          value={it.url}
                                          onChange={(e) => updateItem(idx, { url: e.target.value })}
                                          className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                                          placeholder="https://youtube.com/..."
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">View Mode</label>
                                        <select
                                          value={it.view_mode}
                                          onChange={(e) => updateItem(idx, { view_mode: e.target.value })}
                                          className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                                        >
                                          <option value="new_tab">Open in new tab</option>
                                          <option value="embed">View inside training</option>
                                        </select>
                                      </div>
                                    </>
                                  ) : (
                                    <div className="md:col-span-2">
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Select Installation Manual</label>
                                      <select
                                        value={it.manual_id || ''}
                                        onChange={(e) => {
                                          const id = e.target.value;
                                          const m = (manuals || []).find(x => x.id === id);
                                          updateItem(idx, { manual_id: id, title: m?.title || it.title || 'Installation Manual' });
                                        }}
                                        className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                                      >
                                        <option value="">Select manual...</option>
                                        {(manuals || []).map((m) => (
                                          <option key={m.id} value={m.id}>{m.title}</option>
                                        ))}
                                      </select>
                                      <div className="text-[11px] text-gray-500 mt-1">This will open the manual from Production → Installation Manuals.</div>
                                    </div>
                                  )}
                                </div>
                                <div className="mt-3 flex justify-end">
                                  <button type="button" onClick={() => removeItem(idx)} className="px-3 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm">Remove</button>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>

                      <div className="flex items-center justify-end gap-3">
                        <button type="button" onClick={() => setShowModuleForm(false)} className="px-4 py-2 rounded-md bg-gray-100 hover:bg-gray-200">Cancel</button>
                        <button type="submit" disabled={savingModule} className="px-4 py-2 rounded-md bg-emerald-700 hover:bg-emerald-800 disabled:bg-gray-400 text-white">
                          {savingModule ? 'Saving...' : 'Save Module'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="p-6">
              {loadingMy ? (
                <div className="text-gray-600">Loading your training...</div>
              ) : myModules.length === 0 ? (
                <div className="text-gray-600">No training modules have been assigned to you yet.</div>
              ) : (
                <div className="space-y-4">{myModules.map(renderEmployeeModule)}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Management Home Page Component

export { TrainingModule };
